import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { withLatestFrom, switchMap, catchError, of, map } from "rxjs";
import { ContactClient } from "src/api-service";
import { selectContactState } from "../contact.selectors";
import { ContactsActions } from "./contacts.actions";
import { normalizeError } from "../../../app-store-error.types";

// ============================================================================
// CONTACTS LIST EFFECTS - Server-side filtering, sorting and pagination
// ============================================================================

@Injectable()
export class ContactsEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly contactClient = inject(ContactClient);

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContactsActions.load),
            withLatestFrom(
                this.store.select(selectContactState),
            ),
            switchMap(([action, state]) => {
                // Merge: action params → current state → defaults
                const params = {
                    pagination: {
                        pageIndex: action.pagination?.pageIndex ?? state?.pageNumber ?? 1,
                        pageSize: action.pagination?.pageSize ?? state?.pageSize ?? 50,
                    },
                    sorting: {
                        sortProperty: action.sorting?.sortProperty ?? state?.sortProperty ?? "created",
                        sortAscending: action.sorting?.sortAscending ?? state?.sortAscending ?? false,
                    },
                    searchFilter: action.searchFilter ?? state?.searchFilter ?? "",
                };

                return this.contactClient
                    .getAll(
                        params.pagination.pageIndex,
                        params.pagination.pageSize,
                        params.searchFilter || null,
                        params.sorting.sortProperty,
                        params.sorting.sortAscending,
                        null,
                    )
                    .pipe(
                        map(response => ContactsActions.loadSuccess({
                            contacts: response.data,
                            totalCount: response.pagination.totalItemCount,
                            params,
                        })),
                        catchError(error => of(ContactsActions.loadFailure({
                            error: normalizeError(error),
                            toastKey: "CONTACT_LIST.TOAST.LOAD_ERROR",
                        }))),
                    );
            }),
        ),
    );
}
